import { Component, OnInit, OnDestroy } from '@angular/core';
import {
  TenantNotificationsService, TenantNotificationRow, NotificationStatus,
  notificationTarget, notificationWho, notificationSubline,
  // v1.6.1817 — Asana 1219845145512005: a security alert has to be
  // recognisable in the list at a glance.
  notificationIcon, notificationIconTone, securityAlertRemovedNote,
} from '../../../../core/services/tenant-notifications.service';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { AuthService } from '../../../../core/services/auth.service';
import { TenantService, TenantInstance } from '../../../../core/services/tenant.service';
import { TenantUser } from '../../../../core/models/api.models';
import { isDefaultStore, applyDefaultLocally } from '../../../../core/utils/default-store.utils';
import { StoreContextService } from '../../../../core/services/store-context.service';
import { StoresService } from '../../../../core/services/stores.service';
import { FeatureSuggestionsService, FeatureSuggestionType, FeatureSuggestionPriority } from '../../../../core/services/feature-suggestions.service';
import { StoreFrontendUrlsService, StoreFrontendUrl } from '../../../../core/services/store-frontend-urls.service';
import { AnnouncementsService, Announcement } from '../../../../core/services/announcements.service';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule],
  template: `
    <div class="fixed top-0 left-0 right-0 h-16 bg-white border-b border-gray-200 z-50 flex items-center justify-between px-4 md:px-6">
      <!-- Mobile Menu Button -->
      <button (click)="toggleMobileMenu()" class="md:hidden p-2 rounded-lg hover:bg-gray-100">
        <i class="fa-solid fa-bars text-xl"></i>
      </button>
      
      <!-- Logo -->
      <div class="flex items-center space-x-6">
        <div class="flex items-center">
          <img *ngIf="tenantLogo" [src]="tenantLogo" alt="Logo" class="h-8 w-auto">
          <img *ngIf="!tenantLogo" src="https://happymealprep.com/wp-content/uploads/2020/06/cropped-logo-happy-min-1.png"
               alt="Happy Meal Prep" class="h-8 w-auto">
        </div>
      </div>
      
      <!-- User Menu + Right-aligned actions -->
      <div class="flex items-center space-x-2 md:space-x-3">
        <!-- Academy / Schedule / Ticket — right-aligned -->
        <div class="hidden lg:flex items-center space-x-1 mr-1">
          <a routerLink="/academy" class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 transition">
            <i class="fa-solid fa-graduation-cap text-emerald-500 text-xs"></i>Academy
          </a>
          <!-- v1.6.634 — Pre-fix Schedule and Ticket were dead <button>
               elements with no click handler. Now anchor tags:
                 Schedule → support-calls Calendly (new tab)
                 Ticket   → mailto support@Happymealprep.com
               Calendly opens in a new tab so the operator doesn't lose
               their dashboard state. mailto hands off to the OS mail
               client. -->
          <a href="https://calendly.com/angelo-happydemos/support-calls"
             target="_blank" rel="noopener noreferrer"
             title="Book a support call"
             class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 transition">
            <i class="fa-solid fa-calendar-check text-blue-400 text-xs"></i>Schedule
          </a>
          <a href="mailto:support@Happymealprep.com"
             title="Email support"
             class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 transition">
            <i class="fa-solid fa-ticket text-purple-400 text-xs"></i>Ticket
          </a>
          <!-- v1.6.1441 — inbound SMS reply inbox (Asana 1217416837859037).
               Placed in the header because a reply can arrive while staff are
               anywhere in the panel. The BADGE is polled, never push-driven:
               push is best effort and one device per user wins, so the badge has
               to be correct on its own. -->
          <div class="relative">
            <button type="button" (click)="toggleInbox()"
                    [attr.aria-label]="inboxUnread > 0 ? inboxUnread + ' unread notifications' : 'Notifications'"
                    class="relative flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 transition">
              <i class="fa-regular fa-bell text-amber-500 text-xs"></i>
              <span *ngIf="inboxUnread > 0"
                    class="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                {{ inboxUnread > 99 ? '99+' : inboxUnread }}
              </span>
            </button>

            <div *ngIf="inboxOpen"
                 class="absolute right-0 mt-2 w-[380px] max-h-[70vh] bg-white rounded-xl border border-gray-200 shadow-xl z-50 flex flex-col overflow-hidden">
              <div class="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                <!-- v1.6.1792 — "Notifications", not "Customer replies": this bell now
                     carries new orders, out-of-stock and store-holiday alerts as well
                     as texting replies (Asana 1219431449527924). -->
                <p class="text-sm font-bold text-gray-900 m-0">Notifications</p>
                <button type="button" (click)="markAllInboxRead()" [disabled]="inboxBusy || !inboxUnread"
                        class="text-xs font-semibold text-[#64d2d3] hover:underline disabled:opacity-40 disabled:no-underline">
                  Mark all read
                </button>
              </div>

              <div class="flex items-center gap-1 px-3 py-2 border-b border-gray-100">
                <button *ngFor="let f of ['unread','all']" type="button" (click)="setInboxFilter(f)"
                        class="px-2.5 py-1 rounded-lg text-xs font-semibold capitalize transition"
                        [ngClass]="inboxFilter === f ? 'bg-[#64d2d3]/15 text-[#2a9d9e]' : 'text-gray-500 hover:bg-gray-100'">
                  {{ f }}
                </button>
              </div>

              <div class="flex-1 overflow-y-auto">
                <p *ngIf="inboxLoading" class="px-4 py-6 text-center text-xs text-gray-400 m-0">Loading…</p>
                <p *ngIf="!inboxLoading && !inboxRows.length" class="px-4 py-8 text-center text-xs text-gray-400 m-0">
                  {{ inboxFilter === 'unread' ? 'No unread notifications.' : 'No notifications yet.' }}
                </p>

                <button *ngFor="let n of inboxRows" type="button" (click)="openInboxRow(n)"
                        class="w-full text-left px-4 py-3 border-b border-gray-50 last:border-0 hover:bg-gray-50 transition flex gap-3"
                        [ngClass]="!n.read_at ? 'bg-teal-50/40' : ''">
                  <span class="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0"
                        [ngClass]="!n.read_at ? 'bg-[#64d2d3]' : 'bg-transparent'"></span>
                  <!-- v1.6.1817 — Asana 1219845145512005. Every row looked the
                       same, so an alert saying a checkout was paused was
                       indistinguishable from a texting reply. Only the
                       security alert is coloured: colour everything and
                       there is no emphasis left for the one that matters. -->
                  <span class="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
                        [ngClass]="rowIconTone(n)">
                    <i class="text-xs" [ngClass]="rowIcon(n)"></i>
                  </span>
                  <span class="min-w-0 flex-1">
                    <span class="block text-sm font-semibold text-gray-900 truncate">{{ who(n) }}</span>
                    <!-- v1.6.1771 — Asana 1219431449527924: the number under the
                         name, smaller and softer. Blank when the number IS the
                         name, so it is never printed twice. -->
                    <span *ngIf="whoPhone(n)" class="block text-[11px] text-gray-400 truncate">{{ whoPhone(n) }}</span>
                    <!-- The excerpt is capped server-side; the full body stays in
                         the conversation, which is the PII boundary. -->
                    <span class="block text-xs text-gray-500 line-clamp-2">{{ n.data?.excerpt || n.message }}</span>
                    <!-- Timestamps arrive already formatted for the tenant —
                         parsing and reformatting would shift them. -->
                    <!-- The orders the card processor flagged and the platform
                         removed. They vanish from the order lists, so if this
                         line does not name them nothing else will — and they
                         still have to be refunded in the processor's portal. -->
                    <span *ngIf="removedNote(n)" class="block text-[11px] font-semibold text-amber-700 mt-0.5">
                      {{ removedNote(n) }}
                    </span>
                    <span class="block text-[11px] text-gray-400 mt-0.5">{{ n.data?.received_at || n.created_at }}</span>
                  </span>
                </button>
              </div>

              <div *ngIf="inboxLastPage > 1" class="px-4 py-2 border-t border-gray-100 flex items-center justify-between">
                <button type="button" (click)="inboxPage(inboxCurrentPage - 1)" [disabled]="inboxCurrentPage <= 1"
                        class="text-xs text-gray-500 disabled:opacity-30">Prev</button>
                <span class="text-[11px] text-gray-400">Page {{ inboxCurrentPage }} of {{ inboxLastPage }}</span>
                <button type="button" (click)="inboxPage(inboxCurrentPage + 1)" [disabled]="inboxCurrentPage >= inboxLastPage"
                        class="text-xs text-gray-500 disabled:opacity-30">Next</button>
              </div>
            </div>
          </div>

          <!-- v1.6.661 — Suggest a Feature button. Opens a modal that
               posts to /v1/tenant/feature-suggestions. Spec confirmed
               in the uploaded OpenAPI fragment — backend snapshots
               submitter + tenant from the auth session, so this button
               only needs to collect title / description / type / priority.
               Same visual style as the siblings above, with a lightbulb
               icon to distinguish it from Schedule (calendar) and
               Ticket (email). -->
          <button type="button" (click)="openSuggestionModal()"
                  title="Suggest a feature, report a bug, or share an improvement idea"
                  class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 transition">
            <i class="fa-solid fa-lightbulb text-amber-400 text-xs"></i>Suggest
          </button>
          <!-- v1.6.666 — Announcements bell. Loads on first open (not
               on init) so operators who never open it don't pay the
               request cost. Per the operator note "the announcements
               page is just public", we hit /v1/announcements with the
               tenant token — if the endpoint actually requires super-
               admin scope and returns 403, we degrade silently and the
               button stays available but the panel shows an empty state.
               No backend dep added by this ship. -->
          <button type="button" (click)="openAnnouncementsPanel()"
                  title="View announcements"
                  class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 transition">
            <i class="fa-solid fa-bullhorn text-cyan-500 text-xs"></i>News
          </button>
          <!-- v1.6.662 — Visit Site button (WordPress-style "Visit Site" in
               the admin bar). Surfaces the active store's frontend URL so
               operators can jump to their public-facing storefront in a
               new tab. Backend dep confirmed: GET /v1/tenant/stores/{id}/
               frontend-urls returns { direct_url, frontend_urls[] }; the
               existing StoreFrontendUrlsService wraps it. Three render
               states: hidden when no URLs configured; direct anchor when
               exactly one URL; dropdown menu when multiple environments
               are configured (e.g. production + staging). Loads on store
               context change so the URL always matches the active store. -->
          <ng-container *ngIf="visitSiteOptions.length > 0">
            <!-- Single URL: v1.6.1313 — open programmatically (bypasses Angular's [href]
                 sanitizer, which could silently drop the bound URL and leave a dead link). -->
            <button *ngIf="visitSiteOptions.length === 1" type="button"
               (click)="openStorefront(visitSiteOptions[0].url)"
               [title]="'Open ' + visitSiteOptions[0].url + ' in a new tab'"
               class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 transition">
              <i class="fa-solid fa-arrow-up-right-from-square text-green-500 text-xs"></i>Visit Site
            </button>
            <!-- Multiple URLs: dropdown menu so operator can pick the env -->
            <div *ngIf="visitSiteOptions.length > 1" class="relative">
              <button type="button" (click)="showVisitSiteMenu = !showVisitSiteMenu"
                      title="Visit your storefront"
                      class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 transition">
                <i class="fa-solid fa-arrow-up-right-from-square text-green-500 text-xs"></i>Visit Site
                <i class="fa-solid fa-chevron-down text-[10px] text-gray-400 ml-0.5"></i>
              </button>
              <div *ngIf="showVisitSiteMenu"
                   class="absolute right-0 top-full mt-1 w-72 bg-white border border-gray-200 rounded-xl shadow-lg z-50 py-1.5">
                <p class="px-3 pt-1 pb-1.5 text-[11px] font-semibold text-gray-400 uppercase tracking-wide">
                  Open storefront
                </p>
                <button *ngFor="let opt of visitSiteOptions" type="button"
                   (click)="openStorefront(opt.url); closeVisitSiteMenuDeferred()"
                   class="w-full text-left flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 transition group">
                  <i class="fa-solid fa-arrow-up-right-from-square text-green-500 text-xs shrink-0"></i>
                  <div class="flex-1 min-w-0">
                    <p class="text-sm text-gray-700 truncate">{{ opt.label }}</p>
                    <p class="text-[11px] text-gray-400 truncate">{{ opt.url }}</p>
                  </div>
                </button>
              </div>
            </div>
          </ng-container>
          <div class="h-5 w-px bg-gray-200 mx-1"></div>
        </div>

        <!-- Store / Instance Switcher -->
        <div class="relative">
          <!-- v1.6.345 — More prominent active-store indicator.
               Operators reported wanting an obvious "I am viewing
               this store" signal that doesn't require looking
               carefully at a small button. The picker now has a
               visible teal left border + tiny "Active Store" label
               so it reads like a status badge instead of a generic
               dropdown trigger. -->
          <button (click)="showInstanceMenu = !showInstanceMenu; showUserMenu = false"
                  [attr.aria-label]="'Active store: ' + (currentStoreName || tenantName) + '. Click to switch.'"
                  class="hidden md:flex items-center space-x-2.5 pl-3 pr-3 py-1.5 border-l-4 border-[#62cdcf] border-y border-r border-y-gray-300 border-r-gray-300 rounded-r-lg text-sm hover:bg-[#62cdcf]/5 transition bg-[#62cdcf]/[0.03]">
            <i class="fa-solid fa-store text-[#62cdcf]"></i>
            <div class="flex flex-col items-start text-left leading-tight">
              <span class="text-[9px] font-bold uppercase tracking-wider text-[#3da3a4]">Active Store</span>
              <span class="font-semibold text-gray-800 max-w-[140px] lg:max-w-[220px] xl:max-w-[320px] truncate">{{ currentStoreName || tenantName }}</span>
            </div>
            <i class="fa-solid fa-chevron-down text-gray-400 text-xs ml-1"></i>
          </button>

          <div *ngIf="showInstanceMenu" class="absolute right-0 top-full mt-1 w-80 bg-white rounded-xl shadow-xl border border-gray-200 py-2 z-50">

            <!-- ── Tenant info ── -->
            <div class="px-4 py-2 border-b border-gray-100">
              <div class="text-xs font-bold text-gray-400 uppercase tracking-wide mb-1">Tenant</div>
              <div class="flex items-center gap-2">
                <div class="w-7 h-7 rounded-lg bg-[#64d2d3]/10 flex items-center justify-center shrink-0">
                  <i class="fa-solid fa-building text-[#64d2d3] text-xs"></i>
                </div>
                <div>
                  <p class="text-sm font-semibold text-gray-800">{{ tenantName }}</p>
                  <p class="text-xs text-gray-400">ID: {{ tenantSlug }}</p>
                </div>
              </div>
            </div>

            <!-- ── Store switcher ── -->
            <div class="py-1">
              <div class="px-4 py-1.5 text-xs font-bold text-gray-400 uppercase tracking-wide flex items-center justify-between">
                <span>Stores</span>
                <!-- v1.6.1569 — says what the pin column IS, so the scope does
                     not depend on hovering a tooltip. -->
                <span *ngIf="stores.length > 1" class="font-normal normal-case text-[10px] text-gray-400">
                  pin = everyone's login store
                </span>
                <span *ngIf="storesLoading" class="w-3 h-3 border-2 border-[#64d2d3] border-t-transparent rounded-full animate-spin"></span>
              </div>
              <!-- v1.6.1568 — the row is a flex ROW now rather than a button,
                   so the pin can sit beside the switch action without nesting a
                   button inside a button (invalid, and the inner click never
                   fires reliably). -->
              <ng-container *ngFor="let s of stores">
                <div class="flex items-center w-full hover:bg-gray-50 transition"
                     [ngClass]="s.id === currentStoreId ? 'bg-[#64d2d3]/5' : ''">
                <button (click)="switchToStore(s)"
                        class="flex items-center flex-1 min-w-0 px-4 py-2.5 text-sm">
                  <div class="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mr-3"
                       [ngClass]="s.id === currentStoreId ? 'bg-[#64d2d3] ' : 'bg-gray-100'">
                    <i class="fa-solid fa-store text-xs" [ngClass]="s.id === currentStoreId ? 'text-white' : 'text-gray-400'"></i>
                  </div>
                  <div class="flex-1 text-left min-w-0">
                    <p class="font-semibold text-gray-800 truncate">{{ s.name }}</p>
                    <p class="text-xs text-gray-400 capitalize truncate">{{ (s.type || '').replace('_', ' ') }}</p>
                  </div>
                  <i *ngIf="s.id === currentStoreId" class="fa-solid fa-check text-[#64d2d3] text-xs ml-2"></i>
                </button>

                <!-- v1.6.1568 — "start here when I log in" (Asana 1218461976828933).
                     Setting it does NOT switch the active store: the operator
                     asked for a login default, and moving them mid-session is
                     the thing the spec says not to do. -->
                <!-- v1.6.1569 — the flag is TENANT-WIDE, not per user: the
                     endpoint has no user in the path and only one store can
                     carry it. The wording said "when I log in", which reads as
                     a personal preference for a setting that moves EVERYONE. -->
                <button (click)="toggleDefaultStore(s, $event)" [disabled]="defaultBusyId === s.id"
                        class="shrink-0 px-3 py-2.5 transition disabled:opacity-40"
                        [title]="isDefaultStore(s) ? 'Everyone starts here at login — click to clear'
                                                   : 'Make this the store everyone starts on at login'">
                  <i class="fa-solid fa-thumbtack text-xs"
                     [ngClass]="isDefaultStore(s) ? 'text-[#64d2d3]' : 'text-gray-300 hover:text-gray-500'"></i>
                </button>
                </div>
              </ng-container>
              <div *ngIf="!storesLoading && stores.length === 0" class="px-4 py-2 text-xs text-gray-400 italic">No stores found.</div>
            </div>

            <!-- ── Switch tenant ── -->
            <div *ngIf="recentInstances.length > 1" class="border-t border-gray-100 py-1">
              <div class="px-4 py-1.5 text-xs font-bold text-gray-400 uppercase tracking-wide">Switch Tenant</div>
              <ng-container *ngFor="let inst of recentInstances">
                <button *ngIf="inst.slug !== tenantSlug"
                        (click)="switchInstance(inst)"
                        class="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition">
                  <i class="fa-solid fa-arrow-right-arrow-left text-gray-400 w-5 text-xs"></i>
                  <div class="ml-2 text-left">
                    <p class="font-medium">{{ inst.name }}</p>
                    <p class="text-xs text-gray-400">ID: {{ inst.slug }}</p>
                  </div>
                </button>
              </ng-container>
            </div>

            <div class="border-t border-gray-100 pt-1">
              <button (click)="goToSelectInstance()"
                      class="flex items-center w-full px-4 py-2.5 text-sm text-[#62cdcf] hover:bg-[#62cdcf]/5 transition font-medium">
                <i class="fa-solid fa-plus w-5 text-xs"></i>
                <span class="ml-2">Add Another Tenant</span>
              </button>
            </div>
          </div>
        </div>
        
        <!-- User Avatar -->
        <div class="relative">
          <button (click)="showUserMenu = !showUserMenu; showInstanceMenu = false"
                  class="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded-lg">
            <img [src]="avatarUrl" class="w-8 h-8 md:w-9 md:h-9 rounded-full object-cover" alt="User">
            <div class="hidden sm:block text-sm min-w-0 max-w-[180px]">
              <div class="font-semibold text-gray-800 truncate" [title]="userName">{{ userName }}</div>
              <div class="text-xs text-gray-500 truncate" [title]="userEmail">{{ userEmail }}</div>
            </div>
            <i class="fa-solid fa-chevron-down text-gray-400 text-xs ml-1 hidden sm:block"></i>
          </button>

          <div *ngIf="showUserMenu" class="absolute right-0 top-full mt-1 w-56 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50">
            <div class="px-4 py-2 border-b border-gray-100">
              <div class="font-semibold text-gray-800 text-sm truncate" [title]="userName">{{ userName }}</div>
              <div class="text-xs text-gray-500 truncate" [title]="userEmail">{{ userEmail }}</div>
            </div>
            <a routerLink="/profile" (click)="showUserMenu = false"
               class="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition">
              <i class="fa-solid fa-user-circle w-5 text-gray-400"></i><span class="ml-2">My Profile</span>
            </a>
            <a routerLink="/settings/general" (click)="showUserMenu = false"
               class="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition">
              <i class="fa-solid fa-gear w-5 text-gray-400"></i><span class="ml-2">Settings</span>
            </a>
            <a routerLink="/dev/api-log" (click)="showUserMenu = false"
               class="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition">
              <i class="fa-solid fa-terminal w-5 text-gray-400"></i><span class="ml-2">API Debug Console</span>
            </a>
            <div class="border-t border-gray-100 mt-1 pt-1">
              <button (click)="logout()"
                      class="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition">
                <i class="fa-solid fa-right-from-bracket w-5 text-gray-400"></i><span class="ml-2">Sign Out</span>
              </button>
              <button (click)="switchStore()"
                      class="flex items-center w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition">
                <i class="fa-solid fa-rotate w-5"></i><span class="ml-2">Sign Out &amp; Switch Store</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Click-outside to close all menus -->
    <div *ngIf="showUserMenu || showInstanceMenu" (click)="showUserMenu = false; showInstanceMenu = false" class="fixed inset-0 z-40"></div>

    <!-- v1.6.336 — Store-switch confirmation toast.
         Lives in the header (above all routed content) so it's
         visible even when the operator is mid-navigation between
         the store-detail and parent-list during a switch. -->
    <div *ngIf="switchToast" role="status" aria-live="polite"
         class="fixed top-20 right-6 z-[60] px-4 py-2.5 bg-gray-900 text-white text-sm font-medium rounded-lg shadow-xl flex items-center gap-2.5 animate-in fade-in slide-in-from-top-2">
      <i class="fa-solid fa-store text-[#64d2d3]"></i>
      {{ switchToast }}
    </div>

    <!-- v1.6.666 — Announcements panel. Modal listing published
         announcements pulled from /v1/announcements?status=published.
         Loads on open (showAnnouncementsPanel transitions false→true)
         so operators who never open it don't fetch. Each item shows
         title, image thumbnail, description, and a relative-time
         caption from published_at. Read-only display — no dismiss,
         no mark-read (those fields aren't in the spec, so we can't
         track read state without a backend dep). Backdrop click +
         X button close. -->
    <div *ngIf="showAnnouncementsPanel"
         class="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50"
         (click)="closeAnnouncementsPanel()"
         tabindex="-1">
      <div class="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
           (click)="$event.stopPropagation()">
        <!-- Header -->
        <div class="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <div class="flex items-center gap-2.5">
            <div class="w-9 h-9 rounded-lg bg-cyan-50 flex items-center justify-center">
              <i class="fa-solid fa-bullhorn text-cyan-500"></i>
            </div>
            <div>
              <h2 class="text-base font-bold text-gray-900">Announcements</h2>
              <p class="text-xs text-gray-500">Latest news and updates from the platform</p>
            </div>
          </div>
          <button type="button" (click)="closeAnnouncementsPanel()"
                  class="text-gray-400 hover:text-gray-600 p-1">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
        <!-- Body — scrollable list -->
        <div class="flex-1 overflow-y-auto px-6 py-4">
          <div *ngIf="announcementsLoading" class="flex items-center justify-center py-12 text-gray-400">
            <span class="w-5 h-5 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin mr-2"></span>
            Loading…
          </div>
          <div *ngIf="!announcementsLoading && announcementsError" class="flex items-start gap-2.5 px-3 py-2.5 bg-red-50 border border-red-200 rounded-lg text-sm">
            <i class="fa-solid fa-triangle-exclamation text-red-600 mt-0.5 shrink-0"></i>
            <div class="flex-1 text-red-800">{{ announcementsError }}</div>
          </div>
          <div *ngIf="!announcementsLoading && !announcementsError && announcements.length === 0"
               class="flex flex-col items-center justify-center py-12 text-gray-400">
            <i class="fa-solid fa-inbox text-3xl mb-2"></i>
            <p class="text-sm">No announcements yet.</p>
          </div>
          <ul *ngIf="!announcementsLoading && !announcementsError && announcements.length > 0" class="space-y-4">
            <li *ngFor="let a of announcements"
                class="border border-gray-100 rounded-xl p-4 hover:border-gray-200 transition">
              <div class="flex gap-3">
                <!-- Image thumbnail (if present) -->
                <div *ngIf="a.image?.thumbnail_url || a.image?.url" class="w-20 h-20 rounded-lg overflow-hidden bg-gray-100 shrink-0">
                  <img [src]="a.image?.thumbnail_url || a.image?.url" [alt]="a.image?.original_name || ''"
                       class="w-full h-full object-cover">
                </div>
                <div class="flex-1 min-w-0">
                  <h3 class="text-sm font-bold text-gray-900 mb-1">{{ a.title }}</h3>
                  <p class="text-xs text-gray-600 whitespace-pre-line">{{ a.description }}</p>
                  <p *ngIf="a.published_at" class="text-[11px] text-gray-400 mt-2">
                    <i class="fa-regular fa-clock mr-1"></i>{{ a.published_at | date:'mediumDate' }}
                  </p>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- v1.6.661 — Suggest a Feature modal. Renders only while
         showSuggestionModal is true. Backdrop click closes; explicit
         X button + Cancel button also close. ESC handled via the
         (keydown.escape) on the backdrop. z-[100] sits above modals
         (z-50/60) elsewhere so a suggestion can be raised even while
         another flow is open. -->
    <div *ngIf="showSuggestionModal"
         class="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50"
         (click)="closeSuggestionModal()"
         (keydown.escape)="closeSuggestionModal()"
         tabindex="-1">
      <div class="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
           (click)="$event.stopPropagation()">
        <!-- Header -->
        <div class="flex items-start justify-between px-6 py-4 border-b border-gray-100">
          <div class="flex items-center gap-2.5">
            <div class="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center">
              <i class="fa-solid fa-lightbulb text-amber-500"></i>
            </div>
            <div>
              <h2 class="text-base font-bold text-gray-900">Suggest a Feature</h2>
              <p class="text-xs text-gray-500">Feature ideas, bug reports, or improvements</p>
            </div>
          </div>
          <button type="button" (click)="closeSuggestionModal()"
                  class="text-gray-400 hover:text-gray-600 p-1">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <!-- Body -->
        <div class="px-6 py-4 space-y-4">
          <!-- Success state -->
          <div *ngIf="suggestionSuccess"
               class="flex items-start gap-2.5 px-3 py-2.5 bg-green-50 border border-green-200 rounded-lg text-sm">
            <i class="fa-solid fa-circle-check text-green-600 mt-0.5 shrink-0"></i>
            <div class="flex-1 text-green-800">{{ suggestionSuccess }}</div>
          </div>

          <!-- Error state -->
          <div *ngIf="suggestionError"
               class="flex items-start gap-2.5 px-3 py-2.5 bg-red-50 border border-red-200 rounded-lg text-sm">
            <i class="fa-solid fa-triangle-exclamation text-red-600 mt-0.5 shrink-0"></i>
            <div class="flex-1 text-red-800">{{ suggestionError }}</div>
          </div>

          <!-- Type -->
          <div>
            <label class="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1.5">Type</label>
            <div class="grid grid-cols-2 gap-2">
              <button *ngFor="let opt of suggestionTypes" type="button"
                      (click)="suggestion.type = opt.value"
                      [class.bg-amber-50]="suggestion.type === opt.value"
                      [class.border-amber-400]="suggestion.type === opt.value"
                      [class.text-amber-700]="suggestion.type === opt.value"
                      class="px-3 py-2 border border-gray-200 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-50 transition flex items-center gap-2">
                <i [class]="opt.icon"></i>{{ opt.label }}
              </button>
            </div>
          </div>

          <!-- Priority -->
          <div>
            <label class="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1.5">Priority</label>
            <div class="grid grid-cols-3 gap-2">
              <button *ngFor="let opt of suggestionPriorities" type="button"
                      (click)="suggestion.priority = opt.value"
                      [class.bg-amber-50]="suggestion.priority === opt.value"
                      [class.border-amber-400]="suggestion.priority === opt.value"
                      [class.text-amber-700]="suggestion.priority === opt.value"
                      class="px-3 py-2 border border-gray-200 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-50 transition">
                {{ opt.label }}
              </button>
            </div>
          </div>

          <!-- Title -->
          <div>
            <label class="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1.5">
              Title <span class="text-red-500">*</span>
            </label>
            <input type="text" [(ngModel)]="suggestion.title" name="suggestionTitle"
                   maxlength="200"
                   placeholder="e.g. Bulk product import is slow"
                   class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400">
            <div class="flex justify-between mt-1 text-[11px] text-gray-400">
              <span [class.text-red-500]="suggestion.title.length > 0 && suggestion.title.length < 5">
                <ng-container *ngIf="suggestion.title.length > 0 && suggestion.title.length < 5">Min 5 characters</ng-container>
              </span>
              <span>{{ suggestion.title.length }}/200</span>
            </div>
          </div>

          <!-- Description -->
          <div>
            <label class="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1.5">
              Description <span class="text-red-500">*</span>
            </label>
            <textarea [(ngModel)]="suggestion.description" name="suggestionDescription"
                      rows="5" maxlength="5000"
                      placeholder="Please describe what you'd like to see — the more detail, the better."
                      class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"></textarea>
            <div class="flex justify-between mt-1 text-[11px] text-gray-400">
              <span [class.text-red-500]="suggestion.description.length > 0 && suggestion.description.length < 10">
                <ng-container *ngIf="suggestion.description.length > 0 && suggestion.description.length < 10">Min 10 characters</ng-container>
              </span>
              <span>{{ suggestion.description.length }}/5000</span>
            </div>
          </div>

          <!-- Privacy note -->
          <p class="text-[11px] text-gray-400 italic">
            Your name, email, and store info are attached automatically from your session.
          </p>
        </div>

        <!-- Footer -->
        <div class="flex items-center justify-end gap-2 px-6 py-3 border-t border-gray-100 bg-gray-50/50 rounded-b-2xl">
          <button type="button" (click)="closeSuggestionModal()"
                  class="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition">
            {{ suggestionSuccess ? 'Close' : 'Cancel' }}
          </button>
          <button type="button" (click)="submitSuggestion()"
                  [disabled]="suggestionSubmitting || !canSubmitSuggestion()"
                  class="px-4 py-2 text-sm font-semibold text-white bg-amber-500 hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition flex items-center gap-2">
            <span *ngIf="suggestionSubmitting" class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
            <i *ngIf="!suggestionSubmitting" class="fa-solid fa-paper-plane text-xs"></i>
            {{ suggestionSubmitting ? 'Sending…' : 'Submit' }}
          </button>
        </div>
      </div>
    </div>
  `,
  styles: []
})
export class HeaderComponent implements OnInit, OnDestroy {
  mobileMenuOpen = false;
  showUserMenu = false;
  showInstanceMenu = false;

  userName = 'Admin';
  userEmail = '';
  avatarUrl = 'https://ui-avatars.com/api/?name=Admin&background=64d2d3&color=fff';
  tenantName = '';
  tenantSlug = '';
  tenantLogo: string | null = localStorage.getItem('tenantLogoUrl') ?? null;
  recentInstances: TenantInstance[] = [];

  // Store switching
  stores: any[] = [];
  storesLoading = false;
  currentStoreId: number | null = null;
  currentStoreName = '';

  private routerSub!: Subscription;
  private userSub!: Subscription;
  private tenantSub!: Subscription;
  private storeSub!: Subscription;

  // ── v1.6.661 — Feature suggestion modal state ─────────────────────
  // All UI for the Suggest a Feature button lives in this component.
  // Submitter identity + tenant are snapshotted server-side from the
  // auth session (per spec), so the payload only carries the four
  // operator-supplied fields. Reset to defaults whenever the modal
  // closes after a successful submission so the next open is a clean
  // slate; preserved across close-without-submit so accidental ESC
  // doesn't wipe a half-written suggestion.
  showSuggestionModal = false;
  suggestionSubmitting = false;
  suggestionSuccess = '';
  suggestionError = '';

  suggestion: {
    title: string;
    description: string;
    type: FeatureSuggestionType;
    priority: FeatureSuggestionPriority;
  } = {
    title: '',
    description: '',
    type: 'feature_request',
    priority: 'medium',
  };

  // Static option lists for the modal's type + priority pickers.
  // Defined as readonly arrays so the template can *ngFor over them
  // without rebuilding on each change-detection cycle.
  readonly suggestionTypes: Array<{ value: FeatureSuggestionType; label: string; icon: string }> = [
    { value: 'feature_request', label: 'Feature request', icon: 'fa-solid fa-lightbulb text-amber-500 text-xs' },
    { value: 'bug_report',      label: 'Bug report',      icon: 'fa-solid fa-bug text-red-500 text-xs' },
    { value: 'improvement',     label: 'Improvement',     icon: 'fa-solid fa-arrow-trend-up text-blue-500 text-xs' },
    { value: 'other',           label: 'Other',           icon: 'fa-solid fa-comment text-gray-500 text-xs' },
  ];

  readonly suggestionPriorities: Array<{ value: FeatureSuggestionPriority; label: string }> = [
    { value: 'low',    label: 'Low'    },
    { value: 'medium', label: 'Medium' },
    { value: 'high',   label: 'High'   },
  ];

  // ── v1.6.662 — Visit Site button state ────────────────────────────
  // Combined + deduped list of URLs to display in the Visit Site
  // button/dropdown. Built from { direct_url, frontend_urls[] } per
  // store-frontend-urls endpoint, then refreshed on every store
  // context change. The spec guidance says "never cache between
  // sessions" — within a session, a per-store-switch refresh is
  // appropriate (URLs don't typically change without a store
  // reconfiguration).
  visitSiteOptions: Array<{ url: string; label: string }> = [];
  showVisitSiteMenu = false;
  private currentVisitSiteStoreId: number | null = null;

  // ── v1.6.666 — Announcements panel state ──────────────────────────
  // Lazy-loaded: nothing fetched until the operator opens the panel.
  // Per-session caching (within the component lifetime) avoids re-
  // fetching on every reopen. If the endpoint 403s (in case the
  // tenant Bearer token doesn't have super-admin scope) we surface
  // a soft error in the panel rather than hiding the button — the
  // operator at least sees the announcement entry point and gets
  // feedback that nothing's available right now.
  showAnnouncementsPanel = false;
  announcementsLoading = false;
  announcementsError = '';
  announcements: Announcement[] = [];
  private announcementsLoaded = false;

  constructor(
    private router: Router,
    private auth: AuthService,
    private tenantService: TenantService,
    private http: HttpClient,
    private storeCtx: StoreContextService,
    private storesSvc: StoresService,
    private featureSuggestionsSvc: FeatureSuggestionsService,
    private frontendUrlsSvc: StoreFrontendUrlsService,
    private announcementsSvc: AnnouncementsService,
    // v1.6.1441 — appended LAST on purpose. Inserting a parameter mid-list
    // silently re-maps every positional call site: the header spec constructs
    // this component with `new`, so an inserted arg shifted frontendUrls into
    // featureSuggestions and broke four unrelated Visit Site tests.
    private notifSvc: TenantNotificationsService,
  ) {}

  ngOnInit() {
    this.routerSub = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => { this.mobileMenuOpen = false; this.showUserMenu = false; this.showInstanceMenu = false; this.showVisitSiteMenu = false; this.inboxOpen = false; });

    this.startInboxPolling();   // v1.6.1441 — badge is polled, never push-driven

    this.userSub = this.auth.user$.subscribe(user => {
      if (user) {
        this.userName = user.name || 'Admin';
        this.userEmail = user.email || '';
        this.avatarUrl = user.logo_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name || 'Admin')}&background=64d2d3&color=fff`;
      }
    });

    this.tenantSub = this.tenantService.tenant$.subscribe(t => {
      if (t) {
        this.tenantName = t.name;
        this.tenantSlug = t.slug;
        this.loadTenantLogo();
        this.loadStores();
      }
      this.recentInstances = this.tenantService.getRecentInstances();
    });

    // Track current store from context
    this.storeSub = this.storeCtx.context$.subscribe(ctx => {
      this.currentStoreId = ctx.id;
      this.currentStoreName = ctx.name || '';
      // v1.6.662 — Reload Visit Site URLs when store changes. Skip the
      // refetch if the same store id comes through again (the context$
      // subject may emit duplicates from internal refreshes). Closes
      // the dropdown if it happened to be open during a switch.
      this.showVisitSiteMenu = false;
      if (ctx.id != null && ctx.id !== this.currentVisitSiteStoreId) {
        this.currentVisitSiteStoreId = ctx.id;
        this.loadVisitSiteUrls(ctx.id);
      } else if (ctx.id == null) {
        // Store cleared (logout, switch flow) — clear the cached list
        // so the button hides until a new store is active.
        this.currentVisitSiteStoreId = null;
        this.visitSiteOptions = [];
      }
    });
  }

  /**
   * v1.6.662 — Fetch the active store's frontend URLs and dedupe into
   * a single list for the Visit Site button. Combines `direct_url`
   * (single preferred URL) with `frontend_urls[]` (per-environment
   * list). Labels prefer `env_name` when available, else fall back to
   * the host. On error, fail silent — the button just stays hidden;
   * no toast needed since this is a passive enhancement, not an
   * operator-initiated action.
   */
  /**
   * v1.6.1086 — Make a storefront URL safe to put in an `href`.
   *
   * The backend stores frontend URLs however they were typed, and a
   * scheme-less value ("happymealprep.com", "www.shop.com/x") is treated by
   * the browser as a RELATIVE path — so clicking "Visit Site" navigated to
   * `/dashboard/happymealprep.com`, which matches no route: the button
   * appeared to do nothing at all. Force an absolute URL so the link always
   * leaves the admin.
   *
   * Protocol-relative ("//host") and existing http(s) URLs are respected;
   * anything with a different scheme (mailto:, javascript:, etc.) is dropped
   * rather than rendered as a link.
   */
  /**
   * v1.6.1311 — Close the Visit Site menu WITHOUT cancelling the link's navigation.
   * Previously the anchor had `(click)="showVisitSiteMenu = false"`, which removed the
   * <a> from the DOM (via *ngIf) synchronously during the same click event — and tearing
   * down the anchor mid-click cancels the browser's default new-tab navigation, so the
   * link "did nothing". Deferring the close to a macrotask lets the navigation fire first.
   */
  closeVisitSiteMenuDeferred(): void {
    setTimeout(() => { this.showVisitSiteMenu = false; }, 0);
  }

  /**
   * v1.6.1313 — Open a storefront URL in a new tab PROGRAMMATICALLY.
   *
   * The links were bound with `[href]="opt.url"`, which routes through Angular's URL
   * sanitizer; in some cases that dropped the value and left a dead anchor (click did
   * nothing, no console error). Opening via window.open bypasses the sanitizer entirely and
   * behaves identically for the single-URL and dropdown cases. We still normalise to an
   * absolute URL first (a scheme-less value would otherwise resolve against the admin
   * origin). noopener keeps the storefront tab from getting a handle on the admin window.
   */
  openStorefront(url: string | null | undefined): void {
    const u = this.absoluteSiteUrl(url);
    if (!u) return;
    window.open(u, '_blank', 'noopener,noreferrer');
  }

  absoluteSiteUrl(raw: string | null | undefined): string {
    const u = (raw ?? '').trim();
    if (!u) return '';
    if (/^https?:\/\//i.test(u)) return u;      // already absolute
    if (u.startsWith('//')) return 'https:' + u; // protocol-relative
    if (/^[a-z][a-z0-9+.-]*:/i.test(u)) return ''; // some other scheme — not a storefront link
    return 'https://' + u.replace(/^\/+/, '');  // bare host/path → assume https
  }

  private loadVisitSiteUrls(storeId: number): void {
    this.frontendUrlsSvc.getForStore(storeId).subscribe({
      next: (res) => {
        const seen = new Set<string>();
        const out: Array<{ url: string; label: string }> = [];
        const push = (url: string | null | undefined, label: string) => {
          if (!url) return;
          const u = this.absoluteSiteUrl(url);
          if (!u || seen.has(u)) return;
          seen.add(u);
          out.push({ url: u, label });
        };
        // direct_url (if present) is the preferred single-store URL
        push(res.direct_url, 'Storefront');
        for (const fu of res.frontend_urls ?? []) {
          let label = fu.env_name?.trim() || '';
          if (!label) {
            try { label = new URL(this.absoluteSiteUrl(fu.url) || fu.url).host; } catch { label = fu.url; }
          }
          push(fu.url, label);
        }
        this.visitSiteOptions = out;
      },
      error: (err: any) => {
        // v1.6.676 — Previously fully silent. The Visit Site button is
        // a passive enhancement so we still don't surface a toast or
        // error UI (it would be noise for the typical case), but the
        // *console* warning is essential — without it, when an operator
        // asks "where is my Visit Site button?" there's no diagnostic
        // breadcrumb to point at. Now devtools shows the failed call
        // with the HTTP status + backend message so the cause is
        // obvious (403 = missing permission, 404 = route not deployed,
        // 5xx = backend issue).
        // eslint-disable-next-line no-console
        console.warn('[Visit Site] frontend-urls request failed — button will stay hidden until next store switch', {
          storeId,
          status: err?.status,
          message: err?.error?.message ?? err?.message,
          error: err?.error,
        });
        // Silent fail in the UI — keep prior options on transient errors
        // so a momentary 5xx doesn't make the button disappear mid-flow.
        // If it's a persistent 403/404, the prior list will still be
        // accurate or already empty (initial state).
      },
    });
  }

  loadStores() {
    this.storesLoading = true;
    this.storesSvc.list({ per_page: 100 }).subscribe({
      next: (res: any) => {
        const data = res?.data ?? {};
        this.stores = data.stores ?? data.data ?? (Array.isArray(data) ? data : []);
        this.storesLoading = false;
      },
      error: () => { this.storesLoading = false; }
    });
  }

  // ── v1.6.336 — Store-switch toast state ────────────────────────────
  // The toast confirms the switch happened. Visible briefly after
  // `switchToStore()` runs. State is component-local because nothing
  // else needs it.
  switchToast = '';
  private switchToastTimer: any = null;

  /**
   * v1.6.336 — Switch to a different store, keeping the operator on
   * their current page when it's safe to do so.
   *
   * Previous behavior (pre-v1.6.336): always navigated to /dashboard
   * after a store switch. Operators complained about being booted out
   * of whatever they were doing.
   *
   * New behavior:
   *
   * 1. **Detail routes** (e.g. /orders/123, /products/456,
   *    /customers/UUID) — strip the trailing :id segment and navigate
   *    to the parent list (/orders, /products, /customers). Reason:
   *    the numeric/UUID id likely refers to a record in the OLD store
   *    that doesn't exist in the new one, or worse, refers to a
   *    DIFFERENT record (numeric ids are reused per-store in some
   *    backends). Showing the list is the only safe call.
   *
   * 2. **List routes** (e.g. /orders, /products) and settings routes
   *    (/settings/*, /products/categories, etc.) — stay put. Components
   *    subscribed to `storeCtx.context$` refetch with the new store
   *    automatically; the URL is still meaningful.
   *
   * 3. **Toast** — confirms the switch with the new store name.
   *
   * The "is this a detail route" detection is conservative: only the
   * known detail-route patterns trigger the strip. Everything else is
   * left alone. If we miss a pattern, worst case is the user sees a
   * "not found" or empty state and can click back to a list.
   */
  // ── v1.6.1568 — default store (Asana 1218461976828933) ─────────────
  /** Id currently being written, so only that row's pin is disabled. */
  defaultBusyId: number | null = null;

  isDefaultStore = isDefaultStore;

  /**
   * Marks the store the operator lands on at login.
   *
   * Does NOT switch the active store. They asked for a login default, and the
   * spec is explicit that changing it must not move the active store under
   * someone mid-session.
   *
   * The list is RE-FETCHED afterwards because the response carries only the
   * store that was toggled — the previous default is cleared server-side and is
   * absent from the payload, so patching one row locally would leave two pins
   * lit.
   */
  toggleDefaultStore(store: any, ev: Event): void {
    ev.stopPropagation();          // never fall through to switchToStore
    ev.preventDefault();
    const id = Number(store?.id);
    if (!Number.isFinite(id) || this.defaultBusyId != null) return;

    const next = !isDefaultStore(store);
    /**
     * v1.6.1569 — confirmed, because this is TENANT-WIDE.
     *
     * One person clicking a pin changes where every other member of staff lands
     * at their next login. Nothing else in this menu has that reach, and the
     * pin sits beside actions that only affect the person clicking — so the
     * scope has to be stated at the moment of the click.
     */
    const who = String(store?.name ?? 'this store');
    const msg = next
      ? `Everyone in this tenant will start on ${who} the next time they log in.\n\n`
        + 'It does not change the store you are working in right now.\n\nContinue?'
      : `No store will be the default, so everyone falls back to the usual pick at login.\n\n`
        + 'Continue?';
    // eslint-disable-next-line no-alert
    if (!confirm(msg)) return;

    this.defaultBusyId = id;
    // Optimistic, and exactly one store may carry the flag while in flight.
    this.stores = applyDefaultLocally(this.stores, id, next);

    this.storesSvc.setDefault(id, next).subscribe({
      next: () => {
        this.defaultBusyId = null;
        this.loadStores();         // authoritative: clears the previous default
      },
      error: () => {
        this.defaultBusyId = null;
        this.loadStores();         // put the pins back where the server has them
      },
    });
  }

  switchToStore(store: any) {
    this.showInstanceMenu = false;
    const previousStoreName = this.currentStoreName;
    this.storeCtx.switchStore(store);

    const target = this.computeRouteAfterStoreSwitch(this.router.url);
    if (target !== null) {
      this.router.navigateByUrl(target);
    }
    // If target is null, stay on current URL — components refetch via
    // storeCtx subscription automatically.

    this.showSwitchToast(store?.name || 'Store', previousStoreName);
  }

  /**
   * Decide where to send the operator after a store switch.
   * Returns null to stay on the current URL.
   *
   * Patterns that get reduced to their list parent:
   *   /orders/123                  -> /orders
   *   /orders/123/anything         -> /orders
   *   /products/456                -> /products
   *   /products/456/addons         -> /products  (sub-route still tied to old id)
   *   /customers/UUID              -> /customers
   *   /meal-plans/789              -> /meal-plans
   *   /products/categories/12      -> /products/categories
   *   /products/menus/5            -> /products/menus
   *   /products/tags/5             -> /products/tags
   *   /products/badges/5           -> /products/badges
   *   /products/global-addons/5    -> /products/global-addons
   *   /labels/templates/5/edit     -> /labels/templates
   *   /coupons/abc                 -> /coupons
   *
   * Everything else stays put. Settings routes stay (they're tenant-
   * scoped, not store-scoped, so changing store doesn't affect them
   * meaningfully). List routes stay (they refetch).
   *
   * Exposed as a public method (rather than private) so it can be
   * unit-tested without wiring up the full component.
   */
  computeRouteAfterStoreSwitch(currentUrl: string): string | null {
    // Strip the query string + fragment for matching; we'll re-attach
    // them only if we're staying put (we don't want to carry e.g.
    // `?orderId=123` onto the parent list page after a strip).
    const url = (currentUrl || '/').split('?')[0].split('#')[0];

    // Detail-route patterns: the captured group is the parent list.
    // Order matters — most specific first. Patterns exclude `/new`
    // because those URLs are form pages, not detail pages.
    const detailPatterns: RegExp[] = [
      /^(\/orders)\/(?!new$|bag-scanner$|batch-processor$|bag-to-points$|failed-refunds$)[^/]+/,
      /^(\/products\/categories)\/(?!new$)[^/]+/,
      /^(\/products\/menus)\/(?!new$)[^/]+/,
      /^(\/products\/tags)\/(?!new$)[^/]+/,
      /^(\/products\/badges)\/(?!new$)[^/]+/,
      /^(\/products\/global-addons)\/(?!new$)[^/]+/,
      /^(\/products)\/(?!new$|categories$|menus$|tags$|badges$|global-addons$|recipes$|settings$|ai-import$)[^/]+/,
      /^(\/customers)\/(?!new$)[^/]+/,
      /^(\/meal-plans)\/(?!new$)[^/]+/,
      /^(\/labels\/templates)\/(?!new$)[^/]+/,
      /^(\/coupons)\/(?!new$)[^/]+/,
      /^(\/subscriptions)\/(?!settings$|split-settings$|cancellation-survey$)[^/]+/,
    ];
    for (const pat of detailPatterns) {
      const m = url.match(pat);
      if (m) return m[1];
    }
    // Stay on current page (return null = no navigation).
    return null;
  }

  private showSwitchToast(storeName: string, previousStoreName: string): void {
    if (this.switchToastTimer) clearTimeout(this.switchToastTimer);
    this.switchToast = previousStoreName && previousStoreName !== storeName
      ? `Switched to ${storeName}`
      : `Now viewing ${storeName}`;
    this.switchToastTimer = setTimeout(() => { this.switchToast = ''; }, 3000);
  }

  loadTenantLogo() {
    this.http.get<any>(`${this.tenantService.apiBaseUrl}/v1/tenant/info`).subscribe({
      next: (res: any) => {
        const logo = res?.data?.logo ?? res?.logo;
        this.tenantLogo = logo?.url ?? logo?.medium_url ?? null;
        // Cache so next page load shows it instantly with no flash
        if (this.tenantLogo) localStorage.setItem('tenantLogoUrl', this.tenantLogo);
        else localStorage.removeItem('tenantLogoUrl');
      },
      error: () => { this.tenantLogo = null; }
    });
  }


  // ── v1.6.1441 — inbound reply inbox (Asana 1217416837859037) ──────
  /**
   * The badge is POLLED, deliberately. Abdelrhman's constraint is that push is
   * best effort — and because only one device token per staff member is stored
   * (last registered wins), a member on desktop and laptop gets push on just
   * one. So the badge must be correct without any push ever arriving.
   *
   * Two minutes matches the server-side poller cadence: a faster client poll
   * cannot surface a reply the server has not ingested yet, it just adds load.
   */
  private static readonly INBOX_POLL_MS = 120_000;

  inboxUnread = 0;
  inboxOpen = false;
  inboxRows: TenantNotificationRow[] = [];
  inboxLoading = false;
  inboxBusy = false;
  inboxFilter: NotificationStatus = 'unread';
  inboxCurrentPage = 1;
  inboxLastPage = 1;
  private inboxTimer: any = null;
  private inboxSub?: Subscription;

  who(n: TenantNotificationRow): string { return notificationWho(n); }
  /** v1.6.1771 — the phone, for the softer line under the name. */
  whoPhone(n: TenantNotificationRow): string { return notificationSubline(n); }

  /**
   * v1.6.1817 — the per-type icon and its tone.
   *
   * Methods returning STRINGS, which is safe: the template-stability rule is
   * about bindings that BUILD an array or object, because a fresh identity
   * every change-detection pass re-renders forever. A primitive compares
   * equal. (v1.6.1604, 1616, 1660, 1790, 1800.)
   */
  rowIcon(n: TenantNotificationRow): string { return notificationIcon(n); }
  rowIconTone(n: TenantNotificationRow): string { return notificationIconTone(n); }
  removedNote(n: TenantNotificationRow): string { return securityAlertRemovedNote(n); }

  private startInboxPolling(): void {
    this.refreshInboxCount();
    this.inboxTimer = setInterval(() => this.refreshInboxCount(), HeaderComponent.INBOX_POLL_MS);
  }

  private refreshInboxCount(): void {
    this.inboxSub?.unsubscribe();
    this.inboxSub = this.notifSvc.unreadCount().subscribe(n => {
      // -1 signals a failed poll: keep the last known badge rather than
      // flashing 0, which would read as "all caught up".
      if (n >= 0) this.inboxUnread = n;
    });
  }

  toggleInbox(): void {
    this.inboxOpen = !this.inboxOpen;
    if (this.inboxOpen) this.loadInbox(1);
  }

  setInboxFilter(f: string): void {
    this.inboxFilter = f as NotificationStatus;
    this.loadInbox(1);
  }

  inboxPage(page: number): void {
    if (page < 1 || page > this.inboxLastPage) return;
    this.loadInbox(page);
  }

  private loadInbox(page: number): void {
    this.inboxLoading = true;
    this.notifSvc.list({ status: this.inboxFilter, page, per_page: 15 }).subscribe({
      next: (res) => {
        this.inboxRows = res.rows;
        this.inboxLastPage = res.lastPage;
        this.inboxCurrentPage = page;
        this.inboxLoading = false;
      },
      error: () => { this.inboxRows = []; this.inboxLoading = false; },
    });
  }

  /**
   * Open a reply. Marks the row read, then navigates.
   *
   * A 404 on the mark is NORMAL — the row is not theirs or already gone — so it
   * re-fetches rather than showing an error, and it still navigates: the
   * operator asked to see the conversation, and a bookkeeping failure should not
   * block that.
   */
  openInboxRow(n: TenantNotificationRow): void {
    const target = notificationTarget(n);
    if (!n.read_at) {
      this.notifSvc.markRead(n.id).subscribe(res => {
        if (res.ok) {
          n.read_at = new Date().toISOString();
          this.inboxUnread = Math.max(0, this.inboxUnread - 1);
        } else if (res.gone) {
          this.loadInbox(this.inboxCurrentPage);
          this.refreshInboxCount();
        }
      });
    }
    this.inboxOpen = false;
    // deep_link is a RELATIVE path, so this is an in-app navigation — never
    // window.location to an absolute URL.
    if (target) this.router.navigateByUrl(target);
  }

  markAllInboxRead(): void {
    this.inboxBusy = true;
    this.notifSvc.markAllRead().subscribe(() => {
      this.inboxBusy = false;
      this.inboxUnread = 0;
      this.loadInbox(1);
    });
  }

  ngOnDestroy() {
    if (this.inboxTimer) clearInterval(this.inboxTimer);   // v1.6.1441
    this.inboxSub?.unsubscribe();
    this.routerSub?.unsubscribe();
    this.userSub?.unsubscribe();
    this.tenantSub?.unsubscribe();
    this.storeSub?.unsubscribe();
    if (this.switchToastTimer) clearTimeout(this.switchToastTimer);
  }

  toggleMobileMenu() {
    this.mobileMenuOpen = !this.mobileMenuOpen;
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('toggle-mobile-menu'));
    }
  }

  logout() {
    this.showUserMenu = false;
    this.auth.logout().subscribe();
  }

  switchStore() {
    this.showUserMenu = false;
    this.auth.logoutAndSwitchInstance();
  }

  switchInstance(inst: TenantInstance) {
    this.showInstanceMenu = false;
    // Clear current auth, switch tenant, redirect to login for new tenant
    this.auth.clearAuth();
    this.tenantService.setTenant(inst);
    this.router.navigate(['/login']);
  }

  goToSelectInstance() {
    this.showInstanceMenu = false;
    this.auth.clearAuth();
    this.tenantService.clearTenant();
    this.router.navigate(['/select-instance']);
  }

  /**
   * v1.6.666 — Open the announcements panel. Lazy-loads on first open
   * so operators who never use it don't pay the request cost. After
   * the first successful load, reopens reuse the cached list (good
   * enough for a session — operators don't expect new announcements
   * to appear without a page refresh).
   */
  openAnnouncementsPanel(): void {
    this.showAnnouncementsPanel = true;
    if (!this.announcementsLoaded && !this.announcementsLoading) {
      this.loadAnnouncements();
    }
  }

  closeAnnouncementsPanel(): void {
    this.showAnnouncementsPanel = false;
  }

  /**
   * v1.6.666 — Fetch published announcements. The endpoint is the
   * super-admin /v1/announcements path (no /tenant/ prefix) — operator
   * confirmed it's effectively public for tenant operators. If that
   * assumption is wrong and the call 403s, the panel shows a soft
   * error rather than blowing up the header.
   *
   * per_page=20 caps the inbox size — far more than any tenant will
   * realistically need to browse from a header panel. If the platform
   * starts publishing hundreds, a dedicated /announcements route with
   * proper pagination is the right next step.
   */
  private loadAnnouncements(): void {
    this.announcementsLoading = true;
    this.announcementsError = '';
    this.announcementsSvc.listPublished({ per_page: 20 }).subscribe({
      next: (res) => {
        this.announcements = res.announcements ?? [];
        this.announcementsLoaded = true;
        this.announcementsLoading = false;
      },
      error: (err) => {
        this.announcementsLoading = false;
        // 403 = tenant token doesn't have scope; show a low-key empty
        // state. 404 = endpoint not deployed on this environment; same
        // soft handling. Other errors get a generic message. We don't
        // log warnings here because this is best-effort surfacing of
        // platform news, not a critical workflow.
        if (err?.status === 403 || err?.status === 404) {
          this.announcements = [];
          this.announcementsLoaded = true; // don't keep retrying
        } else {
          this.announcementsError = 'Could not load announcements. Please try again later.';
        }
      },
    });
  }

  // ── v1.6.661 — Feature Suggestion modal handlers ──────────────────

  /**
   * Open the modal. Clears any prior success/error state from a
   * previous open. Keeps the operator's in-progress draft intact
   * unless we're opening after a successful submission (in which
   * case we reset to defaults via the success-close path below).
   */
  openSuggestionModal(): void {
    this.suggestionSuccess = '';
    this.suggestionError = '';
    this.showSuggestionModal = true;
  }

  /**
   * Close the modal. When called after a successful submission
   * (suggestionSuccess populated), also reset the form fields so
   * the next open is a clean slate. When closing mid-edit, leave
   * the draft intact — operator may reopen and finish later.
   */
  closeSuggestionModal(): void {
    if (this.suggestionSuccess) {
      this.suggestion = {
        title: '',
        description: '',
        type: 'feature_request',
        priority: 'medium',
      };
      this.suggestionSuccess = '';
    }
    this.suggestionError = '';
    this.showSuggestionModal = false;
  }

  /**
   * Front-end mirror of the server's validation rules so the submit
   * button can be properly disabled until inputs are valid.
   *   - title:       min 5, max 200
   *   - description: min 10, max 5000
   *   - type, priority: always present (defaults in initial state)
   * Backend will still validate independently; this is purely UX.
   */
  canSubmitSuggestion(): boolean {
    const t = (this.suggestion.title ?? '').trim();
    const d = (this.suggestion.description ?? '').trim();
    if (t.length < 5 || t.length > 200) return false;
    if (d.length < 10 || d.length > 5000) return false;
    if (!this.suggestion.type || !this.suggestion.priority) return false;
    return true;
  }

  /**
   * POST to /v1/tenant/feature-suggestions. The submitter + tenant
   * fields are populated server-side from the auth session — the
   * client deliberately doesn't try to send them (per spec).
   *
   * Success: surface the server's `message` (or a default) as an
   * inline banner, lock the form, swap Cancel → Close. The operator
   * can dismiss; subsequent openSuggestionModal() resets to defaults.
   *
   * Failure: surface a clear error. For 422 we extract the first
   * field-level message via the shared extractError pattern. For
   * other statuses we fall back to a generic message. The form
   * stays open + populated so the operator can edit and retry.
   */
  submitSuggestion(): void {
    if (!this.canSubmitSuggestion() || this.suggestionSubmitting) return;
    this.suggestionSubmitting = true;
    this.suggestionError = '';
    this.suggestionSuccess = '';

    const payload = {
      title:       this.suggestion.title.trim(),
      description: this.suggestion.description.trim(),
      type:        this.suggestion.type,
      priority:    this.suggestion.priority,
    };

    this.featureSuggestionsSvc.submit(payload).subscribe({
      next: (res: any) => {
        this.suggestionSubmitting = false;
        this.suggestionSuccess = res?.message
          ?? 'Thanks — your suggestion was sent to the team.';
      },
      error: (err: any) => {
        this.suggestionSubmitting = false;
        // Extract field-level error if backend returned 422 with
        // errors object; otherwise fall back through message → status.
        const errors = err?.error?.errors;
        if (errors && typeof errors === 'object') {
          const firstKey = Object.keys(errors)[0];
          const firstMsg = Array.isArray(errors[firstKey]) ? errors[firstKey][0] : errors[firstKey];
          this.suggestionError = firstMsg || 'Validation failed — please check your input.';
        } else {
          this.suggestionError = err?.error?.message
            ?? err?.message
            ?? 'Could not send your suggestion. Please try again.';
        }
      },
    });
  }
}
